/**
 * The seeded variation source for sounds (mulberry32). Audio is presentation, so it does not
 * need the sim's Rng and must never draw from it (that would change the cave). It is still
 * seeded, so a sound's variations come out the same in tests. Pure.
 */

/** Uniform floats in [0, 1). */
export type Rand = () => number;

export function mulberry32(seed: number): Rand {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform in [lo, hi). */
export function between(r: Rand, lo: number, hi: number): number {
  return lo + r() * (hi - lo);
}

/** Uniform integer in [0, n). */
export function intBelow(r: Rand, n: number): number {
  return Math.min(n - 1, Math.floor(r() * n));
}

/** `x` scaled by a random factor within +-`amount` (0.1 = ten per cent either way). */
export function vary(r: Rand, x: number, amount: number): number {
  return x * (1 + (r() * 2 - 1) * amount);
}

/**
 * White noise that holds each random value for `hold` samples, like the SID's noise generator
 * clocked slower than the output: played faster or slower, its "pitch" moves as the chip's did.
 */
export function heldNoise(length: number, hold: number, seed: number): Float32Array<ArrayBuffer> {
  const r = mulberry32(seed);
  const out = new Float32Array(length);
  let value = 0;
  for (let i = 0; i < length; i++) {
    if (i % hold === 0) value = r() * 2 - 1;
    out[i] = value;
  }
  return out;
}
